import type {
  MemberSearchFilterParam,
  SearchFilters
} from '@library/types/members';
import { useCallback, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';
import { validate as validateUUID } from 'uuid';

const defaultMemberSearchFilters: SearchFilters = {
  address: '',
  currentSignInIp: '',
  dateOfBirth: '',
  email: '',
  firstName: '',
  inviteCode: '',
  joinDate: undefined,
  lastName: '',
  lastSignInIp: '',
  memberId: '',
  numberOfEntries: '',
  numberOfEntriesType: 'eq',
  postalCode: '',
  pushNotificationToken: '',
  referralCode: '',
  state: '',
  uuid: ''
};

export const useMemberFilters = () => {
  const [searchParams] = useSearchParams();
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);

  const initialSearchFilters = useMemo(() => {
    const searchParamsObj = Object.fromEntries(searchParams.entries());

    const joinDate = searchParamsObj.joinDate
      ? DateTime.fromISO(searchParamsObj.joinDate).toJSDate()
      : undefined;

    return {
      ...defaultMemberSearchFilters,
      ...searchParamsObj,
      joinDate
    };
  }, [searchParams]);

  const [memberSearchFilters, setMemberSearchFilters] =
    useState<SearchFilters>(initialSearchFilters);

  const handleChangeSearchMemberFilter = useCallback(
    ({ key, value }: MemberSearchFilterParam) => {
      setMemberSearchFilters((prevFilters) => {
        if (key === 'memberId') {
          const isValidUUID = validateUUID(String(value));
          return {
            ...prevFilters,
            memberId: isValidUUID ? '' : String(value),
            uuid: isValidUUID ? String(value) : ''
          };
        }

        return {
          ...prevFilters,
          [key]: value
        };
      });
    },
    []
  );

  const handleSubmitSearchMemberFilters = useCallback(
    (searchFilters: SearchFilters) => setMemberSearchFilters(searchFilters),
    []
  );

  const handleResetSearchMemberFilters = useCallback(() => {
    setMemberSearchFilters(defaultMemberSearchFilters);
  }, []);

  const handleToggleFiltersDrawer = useCallback((isOpen?: boolean) => {
    setIsFiltersDrawerOpen(!!isOpen);
  }, []);

  return {
    onChangeSearchMemberFilter: handleChangeSearchMemberFilter,
    onResetSearchMemberFilters: handleResetSearchMemberFilters,
    isFiltersDrawerOpen,
    memberSearchFilters,
    onSubmitSearchMemberFilters: handleSubmitSearchMemberFilters,
    onToggleFiltersDrawer: handleToggleFiltersDrawer
  };
};
